/**
 * @zendir/ui - useCompactMode Hook
 *
 * Provides compact mode functionality for data cards with:
 * - Hover-to-expand behavior
 * - Click-to-pin (stays expanded until clicked again)
 * - Keyboard accessibility (Enter/Space to toggle pin)
 * - Reduced motion support
 * - Integration with DisplaySettingsContext for global compact mode
 *
 * Priority for compact mode:
 * 1. Explicit `compact` prop (if provided)
 * 2. Global compact mode from DisplaySettingsContext
 * 3. Default: false
 *
 * Usage:
 * ```tsx
 * const { expanded, isPinned, handlers, transitionDuration } = useCompactMode({
 *   compact: true, // Optional - if omitted, uses global setting
 *   defaultExpanded: false,
 *   onPinChange: (pinned) => {},
 * });
 *
 * return (
 *   <div {...handlers}>
 *     {expanded ? <FullContent /> : <CompactContent />}
 *   </div>
 * );
 * ```
 */

import { useState, useCallback, useMemo } from "react";
import { useTheme } from "../theme";
import { useDisplaySettingsOptional } from "../context/DisplaySettingsContext";

/**
 * Options for useCompactMode hook
 */
export interface UseCompactModeOptions {
  /** Whether compact mode is enabled */
  compact?: boolean;
  /** Start in expanded (pinned) state */
  defaultExpanded?: boolean;
  /** Callback when pin state changes */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * Result from useCompactMode hook
 */
export interface UseCompactModeResult {
  /** Whether the component should render in expanded state */
  expanded: boolean;
  /** Whether the component is pinned (clicked to stay expanded) */
  isPinned: boolean;
  /** Whether the component is being hovered */
  isHovered: boolean;
  /** Whether compact mode is active */
  isCompact: boolean;
  /** Event handlers to spread on the container element */
  handlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onClick: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    tabIndex: number;
    role: string;
    "aria-expanded": boolean;
  };
  /** Transition duration respecting reduced motion */
  transitionDuration: string;
  /** Toggle the pinned state programmatically */
  togglePin: () => void;
  /** Set pinned state programmatically */
  setPin: (pinned: boolean) => void;
}

/**
 * Hook for managing compact mode state and interactions
 *
 * Provides hover-to-expand and click-to-pin functionality with
 * full keyboard accessibility and reduced motion support.
 *
 * Priority for compact mode:
 * 1. Explicit `compact` prop (if provided)
 * 2. Global compact mode from DisplaySettingsContext
 * 3. Default: false
 */
export function useCompactMode(
  options: UseCompactModeOptions = {},
): UseCompactModeResult {
  const {
    compact: compactProp,
    defaultExpanded = false,
    onPinChange,
  } = options;
  const { prefersReducedMotion } = useTheme();
  const displaySettings = useDisplaySettingsOptional();

  // Resolve compact mode: prop > context > default (false)
  const compact = compactProp ?? displaySettings?.compactMode ?? false;

  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(defaultExpanded);

  // Transition duration respects reduced motion preference
  const transitionDuration = prefersReducedMotion ? "0ms" : "200ms";

  // Expanded state: pinned OR hovered (only matters when compact is true)
  const expanded = !compact || isPinned || isHovered;

  // Toggle pin state
  const togglePin = useCallback(() => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    onPinChange?.(newPinned);
  }, [isPinned, onPinChange]);

  // Set pin state directly
  const setPin = useCallback(
    (pinned: boolean) => {
      setIsPinned(pinned);
      onPinChange?.(pinned);
    },
    [onPinChange],
  );

  // Mouse enter handler
  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  // Mouse leave handler
  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Click handler - toggle pin state when in compact mode
  const onClick = useCallback(() => {
    if (compact) {
      togglePin();
    }
  }, [compact, togglePin]);

  // Keyboard handler for accessibility
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (compact && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        togglePin();
      }
    },
    [compact, togglePin],
  );

  // Memoized handlers object
  const handlers = useMemo(
    () => ({
      onMouseEnter,
      onMouseLeave,
      onClick,
      onKeyDown,
      tabIndex: compact ? 0 : -1,
      role: compact ? "button" : "article",
      "aria-expanded": expanded,
    }),
    [onMouseEnter, onMouseLeave, onClick, onKeyDown, compact, expanded],
  );

  return {
    expanded,
    isPinned,
    isHovered,
    isCompact: compact,
    handlers,
    transitionDuration,
    togglePin,
    setPin,
  };
}
